import { useEffect, useState, useCallback } from 'react';

/**
 * useKeyboardShortcuts - Custom hook for managing keyboard shortcuts
 *
 * @param {Object} shortcuts - Object mapping key combinations to handler functions
 * @param {boolean} enabled - Whether shortcuts are enabled (default: true)
 * @returns {Object} - Helper methods and state
 *
 * Example usage:
 * const shortcuts = {
 *   'ctrl+s': () => handleSave(),
 *   'ctrl+f': () => setSearchOpen(true),
 *   'escape': () => closeModal(),
 *   'arrowup': () => navigateUp(),
 *   'arrowdown': () => navigateDown()
 * };
 *
 * const { showHelp, setShowHelp } = useKeyboardShortcuts(shortcuts);
 */
const useKeyboardShortcuts = (shortcuts = {}, enabled = true) => {
  const [showHelp, setShowHelp] = useState(false);
  const [pressedKeys, setPressedKeys] = useState(new Set());

  // Normalize key name for consistent matching
  const normalizeKey = useCallback((key) => {
    return key.toLowerCase().replace(/\s+/g, '');
  }, []);

  // Build key combination string from event
  const getKeyCombo = useCallback((event) => {
    const keys = [];

    if (event.ctrlKey || event.metaKey) keys.push('ctrl');
    if (event.altKey) keys.push('alt');
    if (event.shiftKey && (event.key || '').length > 1) keys.push('shift'); // Only for non-character keys

    const key = (event.key || '').toLowerCase();
    keys.push(key);

    return keys.join('+');
  }, []);

  // Handle keyboard events
  const handleKeyDown = useCallback((event) => {
    if (!enabled) return;

    // Don't trigger shortcuts when user is typing in an input/textarea
    const target = event.target;
    const isInput = target.tagName === 'INPUT' ||
                   target.tagName === 'TEXTAREA' ||
                   target.contentEditable === 'true';

    // Exception: Allow Ctrl+S, Ctrl+F, Escape even in inputs
    const isExceptionKey = (event.ctrlKey || event.metaKey) &&
                          ['s', 'f'].includes((event.key || '').toLowerCase()) ||
                          event.key === 'Escape';

    if (isInput && !isExceptionKey) return;

    const keyCombo = getKeyCombo(event);

    // Track pressed keys for visualization
    setPressedKeys(prev => new Set([...prev, keyCombo]));

    // Check if this combination has a handler
    const normalizedCombo = normalizeKey(keyCombo);
    const handler = Object.entries(shortcuts).find(([combo]) =>
      normalizeKey(combo) === normalizedCombo
    );

    if (handler) {
      event.preventDefault();
      const shortcutConfig = handler[1];
      // Call the handler function
      if (typeof shortcutConfig === 'function') {
        shortcutConfig(event);
      } else if (shortcutConfig && typeof shortcutConfig.handler === 'function') {
        shortcutConfig.handler(event);
      }
    }

    // Special handling for Ctrl+/ (show shortcuts help)
    if ((event.ctrlKey || event.metaKey) && event.key === '/') {
      event.preventDefault();
      setShowHelp(prev => !prev);
    }
  }, [enabled, shortcuts, getKeyCombo, normalizeKey]);

  const handleKeyUp = useCallback((event) => {
    const keyCombo = getKeyCombo(event);
    setPressedKeys(prev => {
      const newSet = new Set(prev);
      newSet.delete(keyCombo);
      return newSet;
    });
  }, [getKeyCombo]);

  // Register event listeners
  useEffect(() => {
    if (!enabled) return;

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [enabled, handleKeyDown, handleKeyUp]);

  // Helper to format key combo for display
  const formatKeyCombo = useCallback((combo) => {
    return combo
      .split('+')
      .map(key => {
        // Capitalize first letter
        if (key === 'ctrl') return '⌘/Ctrl';
        if (key === 'alt') return 'Alt';
        if (key === 'shift') return 'Shift';
        if (key === 'arrowup') return '↑';
        if (key === 'arrowdown') return '↓';
        if (key === 'arrowleft') return '←';
        if (key === 'arrowright') return '→';
        if (key === 'enter') return 'Enter';
        if (key === 'escape') return 'Esc';
        return key.toUpperCase();
      })
      .join(' + ');
  }, []);

  // Get list of shortcuts with descriptions
  const getShortcutList = useCallback(() => {
    const defaultShortcuts = [
      { combo: 'Ctrl+/', description: 'Show/hide keyboard shortcuts' },
      { combo: 'Esc', description: 'Close modals or deselect' }
    ];

    const customShortcuts = Object.keys(shortcuts).map(combo => ({
      combo: formatKeyCombo(combo),
      description: shortcuts[combo].description || 'No description'
    }));

    return [...customShortcuts, ...defaultShortcuts];
  }, [shortcuts, formatKeyCombo]);

  return {
    showHelp,
    setShowHelp,
    pressedKeys,
    formatKeyCombo,
    getShortcutList
  };
};

export default useKeyboardShortcuts;
